import React, { useEffect, useState } from 'react';
import {
    Drawer,
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Radio,
    Stack,
    Chip,
    Typography,
    Button,
    Divider,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const API_BASE =
    process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

const emptyForm = {
    id: null,
    name: '',
    gender: 'Male',
    mobile: '',
    aadhar: '',
    epic_no: '',
    vidhansabha: 'Karad South',
    karad: {
        village: '',
        ganName: '',
        zpName: '',
        reference: '',
    },
    other: {
        village: '',
        taluka: '',
        district: '',
    },
    booth_no: '',
    purpose: '',
    status: 'Green',
    remarks: 'Documents Ok',
    v_entry_date: '',
};

export default function VisitorDrawer({
    open,
    onClose,
    editingRecord,
    userid,
    onSaved,
}) {
    const [form, setForm] = useState(emptyForm);

    // load edit data
    useEffect(() => {
        if (editingRecord) {
            setForm(editingRecord);
        } else {
            setForm(emptyForm);
        }
    }, [editingRecord]);

    // auto remarks based on status (same as original)
    useEffect(() => {
        if (form.status === 'Green') {
            setForm((f) => ({ ...f, remarks: 'Documents Ok' }));
        }
        if (form.status === 'Yellow') {
            setForm((f) => ({
                ...f,
                remarks: 'Partial Documents Ok. We can call him/her',
            }));
        }
        if (form.status === 'Red') {
            setForm((f) => ({
                ...f,
                remarks: 'Reason - have some issues',
            }));
        }
    }, [form.status]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('karad.')) {
            const key = name.split('.')[1];
            setForm((f) => ({
                ...f,
                karad: { ...f.karad, [key]: value },
            }));
            return;
        }

        if (name.startsWith('other.')) {
            const key = name.split('.')[1];
            setForm((f) => ({
                ...f,
                other: { ...f.other, [key]: value },
            }));
            return;
        }

        setForm((f) => ({ ...f, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            userid,
            name: form.name,
            gender: form.gender,
            mobile: form.mobile,
            aadhar: form.aadhar,
            epic_no: form.epic_no || null,
            vidhansabha_name: form.vidhansabha,
            village:
                form.vidhansabha === 'Karad South'
                    ? form.karad.village
                    : form.other.village,
            booth_no: form.booth_no || null,
            gan_name: form.karad.ganName || null,
            zp_name: form.karad.zpName || null,
            reference: form.karad.reference || null,
            taluka: form.other.taluka || null,
            district: form.other.district || null,
            purpose: form.purpose,
            status: form.status,
            remarks: form.remarks,
            v_entry_date: form.v_entry_date || null,
        };

        const url = editingRecord
            ? `${API_BASE}/visitors/${editingRecord.id}`
            : `${API_BASE}/visitors`;

        const method = editingRecord ? 'PUT' : 'POST';

        await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        onSaved();
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: { xs: '100vw', sm: 450 },
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                {/* HEADER */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography
                        variant="h6"
                        sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}
                    >
                        {editingRecord ? 'Edit Solar Form' : 'Add New Solar Form'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* FORM BODY */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        overflowY: 'auto',
                        flexGrow: 1,
                        pr: 1,
                    }}
                >
                    <TextField
                        label="Name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        fullWidth
                    />

                    <FormControl fullWidth>
                        <InputLabel>Gender</InputLabel>
                        <Select
                            label="Gender"
                            name="gender"
                            value={form.gender}
                            onChange={handleChange}
                        >
                            <MenuItem value="Male">Male</MenuItem>
                            <MenuItem value="Female">Female</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Mobile No"
                        name="mobile"
                        value={form.mobile}
                        onChange={handleChange}
                        fullWidth
                    />

                    <TextField
                        label="Aadhar No"
                        name="aadhar"
                        value={form.aadhar}
                        onChange={handleChange}
                        fullWidth
                    />

                    <TextField
                        label="Consumer No."
                        name="epic_no"
                        value={form.epic_no}
                        onChange={handleChange}
                        fullWidth
                    />

                    <TextField
                        label="Entry Date"
                        name="v_entry_date"
                        type="date"
                        value={form.v_entry_date}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />

                    <FormControl fullWidth>
                        <InputLabel>Vidhansabha</InputLabel>
                        <Select
                            label="Vidhansabha"
                            name="vidhansabha"
                            value={form.vidhansabha}
                            onChange={handleChange}
                        >
                            <MenuItem value="Karad South">Karad South</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                        </Select>
                    </FormControl>

                    {/* CONDITIONAL BLOCKS */}
                    {form.vidhansabha === 'Karad South' ? (
                        <Box
                            sx={{
                                borderLeft: '3px solid #00acc1',
                                pl: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <TextField
                                label="Village"
                                name="karad.village"
                                value={form.karad.village}
                                onChange={handleChange}
                            />
                            <TextField
                                label="Booth No"
                                name="booth_no"
                                value={form.booth_no}
                                onChange={handleChange}
                            />
                            <TextField
                                label="Gan Name"
                                name="karad.ganName"
                                value={form.karad.ganName}
                                onChange={handleChange}
                            />
                            <TextField
                                label="ZP Name"
                                name="karad.zpName"
                                value={form.karad.zpName}
                                onChange={handleChange}
                            />
                            <TextField
                                label="Reference"
                                name="karad.reference"
                                value={form.karad.reference}
                                onChange={handleChange}
                            />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                borderLeft: '3px solid #8e24aa',
                                pl: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <TextField
                                label="Village"
                                name="other.village"
                                value={form.other.village}
                                onChange={handleChange}
                            />
                            <TextField
                                label="Booth No"
                                name="booth_no"
                                value={form.booth_no}
                                onChange={handleChange}
                            />
                        </Box>
                    )}

                    <TextField
                        label="Taluka"
                        name="other.taluka"
                        value={form.other.taluka}
                        onChange={handleChange}
                        fullWidth
                    />

                    <TextField
                        label="District"
                        name="other.district"
                        value={
                            form.status === 'Green' ? 'Satara' : form.other.district
                        }
                        onChange={handleChange}
                        fullWidth
                    />

                    <FormControl fullWidth>
                        <InputLabel>Purpose of Visit</InputLabel>
                        <Select
                            label="Purpose of Visit"
                            name="purpose"
                            value={form.purpose}
                            onChange={handleChange}
                        >
                            <MenuItem value="Enquiry">Enquiry</MenuItem>
                            <MenuItem value="New">New</MenuItem>
                            <MenuItem value="Re-new">Re-new</MenuItem>
                            <MenuItem value="Claim">Claim</MenuItem>
                            <MenuItem value="Nominee">Nominee</MenuItem>
                            <MenuItem value="Appointment">Appointment</MenuItem>
                            <MenuItem value="Household">Household</MenuItem>
                            <MenuItem value="Essential">Essential</MenuItem>
                            <MenuItem value="Safety">Safety</MenuItem>
                        </Select>
                    </FormControl>

                    {/* STATUS */}
                    <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: 'bold' }}
                        >
                            Enquiry Status
                        </Typography>
                        <RadioGroup
                            row
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                        >
                            <FormControlLabel
                                value="Green"
                                control={<Radio color="success" />}
                                label={
                                    <Chip
                                        label="Green"
                                        sx={{ bgcolor: '#4caf50', color: 'white' }}
                                        size="small"
                                    />
                                }
                            />
                            <FormControlLabel
                                value="Yellow"
                                control={<Radio />}
                                label={
                                    <Chip
                                        label="Yellow"
                                        sx={{ bgcolor: '#fbc02d', color: 'white' }}
                                        size="small"
                                    />
                                }
                            />
                            <FormControlLabel
                                value="Red"
                                control={<Radio color="error" />}
                                label={
                                    <Chip
                                        label="Red"
                                        sx={{ bgcolor: '#f44336', color: 'white' }}
                                        size="small"
                                    />
                                }
                            />
                        </RadioGroup>
                    </Box>

                    <TextField
                        label="Remarks"
                        name="remarks"
                        value={form.remarks}
                        onChange={handleChange}
                        multiline
                        rows={2}
                        fullWidth
                    />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* ACTIONS */}
                <Stack direction="row" spacing={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                    >
                        {editingRecord ? 'Update Record' : 'Save Record'}
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        onClick={() => setForm(emptyForm)}
                    >
                        Reset
                    </Button>
                </Stack>
            </Box>
        </Drawer>
    );
}
